import { useEffect, useState } from 'react';

type UseBreakpointResponseValue = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

interface UseBreakpointResponse {
  value: UseBreakpointResponseValue;
  isMobile: boolean;
  isMobileOrTablet: boolean;
  isTablet: boolean;
  isTabletOrDesktop: boolean;
  isDesktop: boolean;
}

// These should match the '@theme' configuration 'themes/shared/styles/index.css'.
const breakpoints: Record<Exclude<UseBreakpointResponseValue, 'xs'>, number> = {
  sm: 640,
  md: 680,
  lg: 1160,
  xl: 1280,
  '2xl': 1536,
};

function getBreakpoint(width: number): UseBreakpointResponseValue {
  if (width >= breakpoints['2xl']) {
    return '2xl';
  }

  if (width >= breakpoints.xl) {
    return 'xl';
  }

  if (width >= breakpoints.lg) {
    return 'lg';
  }

  if (width >= breakpoints.md) {
    return 'md';
  }

  if (width >= breakpoints.sm) {
    return 'sm';
  }

  return 'xs';
}

function useBreakpoint(): UseBreakpointResponse {
  const [value, setValue] = useState(() => {
    return getBreakpoint(window.innerWidth);
  });

  useEffect(() => {
    const handleResize = () => {
      setValue(getBreakpoint(window.innerWidth));
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return {
    value,

    // Everything below is considered mobile.
    isMobile: ['xs', 'sm'].includes(value),

    // Tablet breakpoint starts at '680px'.
    isMobileOrTablet: ['xs', 'sm', 'md'].includes(value),
    isTablet: ['md'].includes(value),

    // Desktop breakpoint starts at '1160px'.
    isTabletOrDesktop: ['md', 'lg', 'xl', '2xl'].includes(value),
    isDesktop: ['lg', 'xl', '2xl'].includes(value),
  };
}

export { useBreakpoint };
export type { UseBreakpointResponse, UseBreakpointResponseValue };
